import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { OnlinePrediction } from "@mui/icons-material";
import { useResponsive } from "../../../core/hooks/useResponsive";

const money = (value) =>
  value == null || Number.isNaN(Number(value)) ? "—" : Number(value).toFixed(2);

const winColor = (theme, value) => {
  if (value == null) return theme.palette.text.disabled;
  if (value >= 65) return theme.palette.success.main;
  if (value >= 45) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const Win = ({ value, strong = false }) => {
  const theme = useTheme();
  if (value == null) return <span style={{ color: theme.palette.text.disabled }}>—</span>;
  return (
    <span style={{ color: winColor(theme, value), fontWeight: strong ? 800 : 500 }}>
      {Number(value).toFixed(1)}%
    </span>
  );
};

const PredictionGrid = ({ predictions, selectedToken, onSelect, targetGainPct, horizonDays }) => {
  const theme = useTheme();
  const { isCompact } = useResponsive();
  const primary = theme.palette.primary.main;
  const rows = Array.isArray(predictions) ? predictions : [];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <OnlinePrediction sx={{ color: primary, fontSize: 22 }} />
        <Typography variant="subtitle1" fontWeight={800}>
          Symbol prediction
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({rows.length} symbols)
        </Typography>
        {targetGainPct != null && (
          <Chip
            size="small"
            variant="outlined"
            label={
              isCompact
                ? `+${targetGainPct}% / ${horizonDays}d`
                : `trendWin% = chance of +${targetGainPct}% within ${horizonDays} sessions`
            }
            sx={{ ml: { xs: 0, sm: "auto" } }}
          />
        )}
      </Box>
      <TableContainer
        sx={{
          flex: 1,
          maxHeight: { xs: 340, sm: 380, lg: 520 },
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          borderRadius: 2,
          border: `1px solid ${alpha(primary, 0.12)}`,
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: alpha(primary, 0.07),
            fontWeight: 800,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: primary,
            borderBottom: `2px solid ${alpha(primary, 0.12)}`,
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            bgcolor: alpha(primary, 0.06),
            cursor: "pointer",
          },
          "& .MuiTableCell-root": {
            px: { xs: 0.75, md: 1.5 },
            fontSize: { xs: "0.7rem", md: "0.8125rem" },
          },
          // Symbol stays put while the price and win columns scroll under it, so a row is
          // still identifiable on a phone.
          "& .sticky-symbol": {
            position: "sticky",
            left: 0,
            zIndex: 3,
            bgcolor: theme.palette.background.paper,
          },
          "& .MuiTableHead-root .sticky-symbol": {
            zIndex: 4,
          },
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: { xs: 760, md: 900 } }}>
          <TableHead>
            <TableRow>
              {!isCompact && <TableCell>Token</TableCell>}
              <TableCell className="sticky-symbol">Symbol</TableCell>
              <TableCell align="right">{isCompact ? "P.Close" : "Prev close"}</TableCell>
              <TableCell align="right">Open</TableCell>
              <TableCell align="right">High</TableCell>
              <TableCell align="right">Low</TableCell>
              <TableCell align="right">Close</TableCell>
              <TableCell align="right">Curr win</TableCell>
              <TableCell align="right">Prev1</TableCell>
              <TableCell align="right">Prev2</TableCell>
              <TableCell align="right">Prev3</TableCell>
              <TableCell align="right">Prev4</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No predictions yet. Start prediction to begin polling.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const selected = String(row.token) === String(selectedToken);
                return (
                  <TableRow
                    key={row.token}
                    hover
                    selected={selected}
                    onClick={() => onSelect(row.token)}
                    sx={{
                      bgcolor: selected ? alpha(primary, 0.12) : undefined,
                      "& .MuiTableCell-root": { whiteSpace: "nowrap" },
                    }}
                  >
                    {!isCompact && <TableCell>{row.token}</TableCell>}
                    <TableCell
                      className="sticky-symbol"
                      sx={{
                        fontWeight: 700,
                        bgcolor: selected ? alpha(primary, 0.12) : undefined,
                      }}
                    >
                      {row.symbol}
                    </TableCell>
                    <TableCell align="right">{money(row.previousClose)}</TableCell>
                    <TableCell align="right">{money(row.open)}</TableCell>
                    <TableCell align="right">{money(row.high)}</TableCell>
                    <TableCell align="right">{money(row.low)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(row.close)}</TableCell>
                    <TableCell align="right"><Win value={row.currWin} strong /></TableCell>
                    <TableCell align="right"><Win value={row.prev1Win} /></TableCell>
                    <TableCell align="right"><Win value={row.prev2Win} /></TableCell>
                    <TableCell align="right"><Win value={row.prev3Win} /></TableCell>
                    <TableCell align="right"><Win value={row.prev4Win} /></TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PredictionGrid;
